import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API } from "@/App";
import { usePermissions } from "@/hooks/usePermissions";
import axios from "axios";
import { toast } from "sonner";
import {
  Car,
  Plus,
  MagnifyingGlass,
  Funnel,
  CaretRight,
  DownloadSimple,
  Camera,
  Spinner
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const STATUS_LABELS = {
  en_stock: "En stock",
  en_preparation: "En préparation",
  pret_a_vendre: "Prêt à vendre",
  reserve: "Réservé",
  vendu: "Vendu",
  archive: "Archivé"
};

const FUEL_OPTIONS = ["Essence", "Diesel", "Hybride", "Électrique", "GPL", "CNG"];
const TRANSMISSION_OPTIONS = ["Manuelle", "Automatique", "Semi-automatique"];

const INITIAL_VEHICLE = {
  marque: "", modele: "", version: "", vin: "", immatriculation: "",
  kilometrage: 0, carburant: "Diesel", boite: "Manuelle",
  annee: new Date().getFullYear(), couleur: "", prix_achat: 0, frais: 0, prix_vente: 0
};

const VehicleRow = ({ vehicle, canSeePrices }) => (
  <tr key={vehicle.vehicle_id} data-testid={`vehicle-row-${vehicle.vehicle_id}`}>
    <td className="mono">{vehicle.reference_interne}</td>
    <td>
      <p className="font-medium text-slate-900">{vehicle.marque} {vehicle.modele}</p>
      <p className="text-xs text-slate-500">{vehicle.immatriculation || (vehicle.vin?.slice(0, 11) + "...")}</p>
    </td>
    <td>{vehicle.kilometrage?.toLocaleString()} km</td>
    <td>{vehicle.annee}</td>
    {canSeePrices && (
      <td className="font-semibold text-slate-900">
        {vehicle.prix_vente ? `${vehicle.prix_vente.toLocaleString('fr-BE')} EUR` : "-"}
      </td>
    )}
    <td>
      <div className="flex items-center gap-1.5">
        <span className={`badge badge-${vehicle.statut}`}>
          {STATUS_LABELS[vehicle.statut] || vehicle.statut}
        </span>
        {vehicle.type_propriete === "depot_vente" && (
          <span className="px-1.5 py-0.5 text-[9px] rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 font-medium">DV</span>
        )}
      </div>
    </td>
    <td className="text-right">
      <Link to={`/vehicules/${vehicle.vehicle_id}`} data-testid={`view-vehicle-${vehicle.vehicle_id}`}
        className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm">
        Voir <CaretRight size={14} className="ml-0.5" />
      </Link>
    </td>
  </tr>
);

const Vehicles = () => {
  const { can } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("statut") || "all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newVehicle, setNewVehicle] = useState(INITIAL_VEHICLE);
  const [scanning, setScanning] = useState(false);
  const fileInputRef = useRef(null);

  const fetchVehicles = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.statut = statusFilter;
      if (searchTerm) params.search = searchTerm;
      const response = await axios.get(`${API}/vehicles`, { params });
      setVehicles(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      toast.error("Erreur lors du chargement des véhicules");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const handleCreateVehicle = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/vehicles`, newVehicle);
      toast.success("Véhicule créé avec succès");
      setDialogOpen(false);
      setNewVehicle(INITIAL_VEHICLE);
      fetchVehicles();
    } catch (error) {
      console.error("Error creating vehicle:", error);
      toast.error("Erreur lors de la création du véhicule");
    }
  };

  const handleScanRegistration = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be re-selected
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo)");
      return;
    }
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/vehicles/scan-registration`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const f = res.data.fields || {};
      // Merge extracted fields into the vehicle form (keep existing non-empty values)
      setNewVehicle((v) => ({
        ...v,
        marque: f.marque || v.marque,
        modele: f.modele || v.modele,
        version: f.version || v.version,
        vin: f.vin || v.vin,
        immatriculation: f.immatriculation || v.immatriculation,
        annee: f.annee || v.annee,
        carburant: f.carburant || v.carburant,
        couleur: f.couleur || v.couleur
      }));
      toast.success("Carte grise scannée - champs pré-remplis. Vérifiez puis complétez.");
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors du scan";
      toast.error(msg);
    } finally {
      setScanning(false);
    }
  };

  const canCreateVehicle = can("vehicles", "create");
  const canSeePrices = can("vehicles", "read_prices");

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/export/vehicles?format=csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vehicules_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export CSV téléchargé");
    } catch {
      toast.error("Erreur lors de l'export");
    }
  };

  const statusFilterEntries = useMemo(() =>
    Object.entries(STATUS_LABELS).filter(([k]) => k !== 'archive'),
  []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-10 w-10"></div>
      </div>
    );
  }

  return (
    <div data-testid="vehicles-page" className="animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Véhicules</h1>
          <p className="page-subtitle">{total} véhicule{total !== 1 ? "s" : ""}</p>
        </div>
        <div className="page-actions">
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-vehicles-csv">
            <DownloadSimple size={18} className="mr-2" /> Export CSV
          </Button>
          {canCreateVehicle && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-vehicle-btn" className="btn-primary">
                  <Plus size={18} className="mr-2" /> Ajouter un véhicule
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-heading-3">Nouveau véhicule</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateVehicle} className="space-y-4 mt-4">
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment"
                    className="hidden" onChange={handleScanRegistration} data-testid="scan-input" />
                  <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-4 bg-slate-50/50 dark:bg-slate-900/30 flex items-center justify-between gap-3">
                    <div className="text-xs text-slate-600 dark:text-slate-400">
                      <span className="font-semibold text-slate-900 dark:text-slate-100 block mb-0.5">Scanner la carte grise</span>
                      Pré-remplit automatiquement marque, modèle, VIN, immatriculation, année, carburant...
                    </div>
                    <Button type="button" variant="outline" disabled={scanning}
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="scan-registration-btn" className="flex-shrink-0">
                      {scanning ? (<><Spinner size={16} className="mr-2 animate-spin" /> Analyse IA...</>)
                                : (<><Camera size={16} className="mr-2" /> Scanner</>)}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">Marque *</Label>
                      <Input data-testid="input-marque" className="form-input" value={newVehicle.marque}
                        onChange={(e) => setNewVehicle({ ...newVehicle, marque: e.target.value })} required />
                    </div>
                    <div>
                      <Label className="form-label">Modèle *</Label>
                      <Input data-testid="input-modele" className="form-input" value={newVehicle.modele}
                        onChange={(e) => setNewVehicle({ ...newVehicle, modele: e.target.value })} required />
                    </div>
                    <div>
                      <Label className="form-label">Version</Label>
                      <Input data-testid="input-version" className="form-input" value={newVehicle.version}
                        onChange={(e) => setNewVehicle({ ...newVehicle, version: e.target.value })} />
                    </div>
                    <div>
                      <Label className="form-label">VIN / Châssis *</Label>
                      <Input data-testid="input-vin" className="form-input" value={newVehicle.vin}
                        onChange={(e) => setNewVehicle({ ...newVehicle, vin: e.target.value })} required />
                    </div>
                    <div>
                      <Label className="form-label">Immatriculation</Label>
                      <Input data-testid="input-immatriculation" className="form-input" value={newVehicle.immatriculation}
                        onChange={(e) => setNewVehicle({ ...newVehicle, immatriculation: e.target.value })} />
                    </div>
                    <div>
                      <Label className="form-label">Kilométrage *</Label>
                      <Input type="number" data-testid="input-kilometrage" className="form-input" value={newVehicle.kilometrage}
                        onChange={(e) => setNewVehicle({ ...newVehicle, kilometrage: parseInt(e.target.value) || 0 })} required />
                    </div>
                    <div>
                      <Label className="form-label">Carburant</Label>
                      <Select value={newVehicle.carburant} onValueChange={(val) => setNewVehicle({ ...newVehicle, carburant: val })}>
                        <SelectTrigger data-testid="select-carburant"><SelectValue /></SelectTrigger>
                        <SelectContent>{FUEL_OPTIONS.map(opt => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="form-label">Boîte</Label>
                      <Select value={newVehicle.boite} onValueChange={(val) => setNewVehicle({ ...newVehicle, boite: val })}>
                        <SelectTrigger data-testid="select-boite"><SelectValue /></SelectTrigger>
                        <SelectContent>{TRANSMISSION_OPTIONS.map(opt => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="form-label">Année *</Label>
                      <Input type="number" data-testid="input-annee" className="form-input" value={newVehicle.annee}
                        onChange={(e) => setNewVehicle({ ...newVehicle, annee: parseInt(e.target.value) || 2024 })} required />
                    </div>
                    <div>
                      <Label className="form-label">Couleur</Label>
                      <Input data-testid="input-couleur" className="form-input" value={newVehicle.couleur}
                        onChange={(e) => setNewVehicle({ ...newVehicle, couleur: e.target.value })} />
                    </div>
                    <div>
                      <Label className="form-label">Prix d'achat (EUR) *</Label>
                      <Input type="number" step="0.01" data-testid="input-prix-achat" className="form-input" value={newVehicle.prix_achat}
                        onChange={(e) => setNewVehicle({ ...newVehicle, prix_achat: parseFloat(e.target.value) || 0 })} required />
                    </div>
                    <div>
                      <Label className="form-label">Frais (EUR)</Label>
                      <Input type="number" step="0.01" data-testid="input-frais" className="form-input" value={newVehicle.frais}
                        onChange={(e) => setNewVehicle({ ...newVehicle, frais: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div>
                      <Label className="form-label">Prix de vente (EUR)</Label>
                      <Input type="number" step="0.01" data-testid="input-prix-vente" className="form-input" value={newVehicle.prix_vente}
                        onChange={(e) => setNewVehicle({ ...newVehicle, prix_vente: parseFloat(e.target.value) || 0 })} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                    <Button type="submit" data-testid="submit-vehicle-btn" className="btn-primary">Créer le véhicule</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="search-vehicles" placeholder="Rechercher par marque, modèle, VIN, immatriculation..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <div className="flex items-center gap-2">
          <Funnel size={18} className="text-slate-500" />
          <Select value={statusFilter} onValueChange={(val) => {
            setStatusFilter(val);
            if (val === "all") { searchParams.delete("statut"); }
            else { searchParams.set("statut", val); }
            setSearchParams(searchParams);
          }}>
            <SelectTrigger data-testid="filter-status" className="w-48">
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {statusFilterEntries.map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Vehicle List */}
      {vehicles.length === 0 ? (
        <div className="empty-state">
          <Car size={56} className="empty-state-icon" />
          <p className="empty-state-title">Aucun véhicule trouvé</p>
          <p className="empty-state-description">
            {searchTerm || statusFilter !== "all" ? "Modifiez vos critères de recherche" : "Commencez par ajouter un véhicule"}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Référence</th>
                <th>Véhicule</th>
                <th>Kilométrage</th>
                <th>Année</th>
                {canSeePrices && <th>Prix vente</th>}
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map((vehicle) => (
                <VehicleRow key={vehicle.vehicle_id} vehicle={vehicle} canSeePrices={canSeePrices} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Vehicles;
